/**
 * Top Locations
 * 
 * Displays lists of most active locations and racks based on activity intensity:
 * - Top 10 Most Active Locations (by BinCode)
 * - Top 5 Most Active Racks (by rack number)
 * 
 * Sorted by intensity score from HeatmapCalculator.
 * Click to navigate to location/rack.
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 7 Implementation
 */

import React, { useMemo } from 'react';
import { MapPin, TrendingUp, Activity, Award, ChevronRight } from 'lucide-react';
import { Card } from '../../../common/Card';
import { LocationActivityEvent, ActivityType } from '../../../../types/locationActivity';
import { TimeWindow } from './ActivityCacheManager';

interface TopLocationsProps {
  /** All events for calculations */
  events: LocationActivityEvent[];
  
  /** Time window for filtering events */
  timeWindow?: TimeWindow;
  
  /** Number of top locations to show */
  topLocationsCount?: number;
  
  /** Number of top racks to show */
  topRacksCount?: number;
  
  /** Callback when user clicks on a location */
  onLocationClick?: (binCode: string) => void;
  
  /** Callback when user clicks on a rack */
  onRackClick?: (rackNumber: number) => void;
}

interface LocationScore {
  binCode: string;
  rackNumber: number;
  score: number;
  eventCount: number;
  lastEventTime: string;
}

interface RackScore {
  rackNumber: number;
  score: number;
  eventCount: number;
  locationCount: number;
  lastEventTime: string;
}

/**
 * Activity type weight for intensity calculation
 * (matches HeatmapCalculator.ts weights)
 */
const ACTIVITY_WEIGHTS: Record<ActivityType, number> = {
  [ActivityType.CorrectionOut]: 1.5,
  [ActivityType.CorrectionIn]: 1.5,
  [ActivityType.Pick]: 1.0,
  [ActivityType.PutAway]: 1.0,
  [ActivityType.Receiving]: 1.0,
  [ActivityType.Transfer]: 0.5,
  [ActivityType.Counting]: 0.3,
  [ActivityType.Reservation]: 0.2
};

/**
 * Filter events by time window
 */
const filterEventsByTimeWindow = (
  events: LocationActivityEvent[],
  timeWindow: TimeWindow
): LocationActivityEvent[] => {
  const now = Date.now();
  let cutoffTime: number;

  switch (timeWindow) {
    case TimeWindow.Last1Hour:
      cutoffTime = now - 60 * 60 * 1000;
      break;
    case TimeWindow.Last4Hours:
      cutoffTime = now - 4 * 60 * 60 * 1000;
      break;
    case TimeWindow.Last8Hours:
      cutoffTime = now - 8 * 60 * 60 * 1000;
      break;
    case TimeWindow.Today:
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      cutoffTime = today.getTime();
      break;
    default:
      return events;
  }

  return events.filter(event => {
    const eventTime = new Date(event.timestamp).getTime();
    return eventTime >= cutoffTime;
  });
};

/**
 * Calculate weighted intensity score for an event
 */
const calculateEventScore = (event: LocationActivityEvent): number => {
  const weight = ACTIVITY_WEIGHTS[event.activityType] || 1.0;
  return weight;
};

/**
 * Get intensity color based on score
 */
const getIntensityColor = (score: number, maxScore: number): string => {
  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
  
  if (percentage >= 80) return 'bg-red-100 text-red-700 border-red-300';
  if (percentage >= 60) return 'bg-orange-100 text-orange-700 border-orange-300';
  if (percentage >= 40) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  if (percentage >= 20) return 'bg-blue-100 text-blue-700 border-blue-300';
  return 'bg-gray-100 text-gray-700 border-gray-300';
};

/**
 * Format time ago from timestamp
 */
const formatTimeAgo = (timestamp: string): string => {
  const now = Date.now();
  const eventTime = new Date(timestamp).getTime();
  const diffMs = now - eventTime;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Akkurat nå';
  if (diffMins < 60) return `${diffMins} min siden`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}t siden`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d siden`;
};

export const TopLocations: React.FC<TopLocationsProps> = ({
  events,
  timeWindow = TimeWindow.Today,
  topLocationsCount = 10,
  topRacksCount = 5,
  onLocationClick,
  onRackClick
}) => {
  /**
   * Calculate top locations by activity intensity
   */
  const topLocations = useMemo((): LocationScore[] => {
    // Filter by time window
    const filteredEvents = filterEventsByTimeWindow(events, timeWindow);
    
    // Group by binCode and calculate scores
    const locationScores = new Map<string, LocationScore>();
    
    filteredEvents.forEach(event => {
      const existing = locationScores.get(event.binCode);
      const eventScore = calculateEventScore(event);
      
      if (existing) {
        existing.score += eventScore;
        existing.eventCount++;
        // Update to most recent timestamp
        if (new Date(event.timestamp) > new Date(existing.lastEventTime)) {
          existing.lastEventTime = event.timestamp;
        }
      } else {
        locationScores.set(event.binCode, {
          binCode: event.binCode,
          rackNumber: event.rackNumber,
          score: eventScore,
          eventCount: 1,
          lastEventTime: event.timestamp
        });
      }
    });
    
    // Convert to array, sort by score, and return top N
    return Array.from(locationScores.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, topLocationsCount);
  }, [events, timeWindow, topLocationsCount]);

  /**
   * Calculate top racks by activity intensity
   */
  const topRacks = useMemo((): RackScore[] => {
    // Filter by time window
    const filteredEvents = filterEventsByTimeWindow(events, timeWindow);
    
    // Group by rackNumber and calculate scores
    const rackScores = new Map<number, RackScore>();
    
    filteredEvents.forEach(event => {
      const existing = rackScores.get(event.rackNumber);
      const eventScore = calculateEventScore(event);
      
      if (existing) {
        existing.score += eventScore;
        existing.eventCount++;
        // Update to most recent timestamp
        if (new Date(event.timestamp) > new Date(existing.lastEventTime)) {
          existing.lastEventTime = event.timestamp;
        }
      } else {
        rackScores.set(event.rackNumber, {
          rackNumber: event.rackNumber,
          score: eventScore,
          eventCount: 1,
          locationCount: 0, // Will be calculated separately
          lastEventTime: event.timestamp
        });
      }
    });
    
    // Calculate unique locations per rack
    filteredEvents.forEach(event => {
      const rackScore = rackScores.get(event.rackNumber);
      if (rackScore) {
        // Count unique binCodes (simplified approach)
        const rackEvents = filteredEvents.filter(e => e.rackNumber === event.rackNumber);
        rackScore.locationCount = new Set(rackEvents.map(e => e.binCode)).size;
      }
    });
    
    // Convert to array, sort by score, and return top N
    return Array.from(rackScores.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, topRacksCount);
  }, [events, timeWindow, topRacksCount]);

  // Find max scores for color calculation
  const maxLocationScore = topLocations.length > 0 ? topLocations[0].score : 1;
  const maxRackScore = topRacks.length > 0 ? topRacks[0].score : 1;

  return (
    <div className="space-y-4">
      {/* Top Locations */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
          <MapPin className="text-blue-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-900">Top {topLocationsCount} Lokasjoner</h3>
          <Award className="text-yellow-500" size={16} />
        </div>

        {topLocations.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <MapPin className="mx-auto mb-2 text-gray-400" size={32} />
            <p className="text-sm">Ingen aktivitet i valgt tidsperiode</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topLocations.map((location, index) => {
              const intensityColor = getIntensityColor(location.score, maxLocationScore);
              
              return (
                <div
                  key={location.binCode}
                  onClick={() => onLocationClick && onLocationClick(location.binCode)}
                  className={`p-3 rounded-lg border ${intensityColor} ${
                    onLocationClick ? 'cursor-pointer hover:shadow-md' : ''
                  } transition-all`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Rank badge */}
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                        ${index === 0 ? 'bg-yellow-400 text-yellow-900' : 
                          index === 1 ? 'bg-gray-300 text-gray-700' :
                          index === 2 ? 'bg-orange-300 text-orange-900' :
                          'bg-gray-200 text-gray-600'}
                      `}>
                        {index + 1}
                      </div>
                      
                      {/* Location info */}
                      <div>
                        <div className="font-semibold text-sm">{location.binCode}</div>
                        <div className="text-xs opacity-75">
                          Reol {location.rackNumber} • {location.eventCount} hendelser
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Score */}
                      <div className="text-right">
                        <div className="text-lg font-bold">{location.score.toFixed(1)}</div>
                        <div className="text-xs opacity-75">{formatTimeAgo(location.lastEventTime)}</div>
                      </div>
                      
                      {onLocationClick && (
                        <ChevronRight size={16} className="opacity-50" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Top Racks */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
          <TrendingUp className="text-purple-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-900">Top {topRacksCount} Reoler</h3>
          <Award className="text-yellow-500" size={16} />
        </div>

        {topRacks.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <Activity className="mx-auto mb-2 text-gray-400" size={32} />
            <p className="text-sm">Ingen aktivitet i valgt tidsperiode</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topRacks.map((rack, index) => {
              const intensityColor = getIntensityColor(rack.score, maxRackScore);
              
              return (
                <div
                  key={rack.rackNumber}
                  onClick={() => onRackClick && onRackClick(rack.rackNumber)}
                  className={`p-3 rounded-lg border ${intensityColor} ${
                    onRackClick ? 'cursor-pointer hover:shadow-md' : ''
                  } transition-all`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Rank badge */}
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                        ${index === 0 ? 'bg-yellow-400 text-yellow-900' : 
                          index === 1 ? 'bg-gray-300 text-gray-700' :
                          index === 2 ? 'bg-orange-300 text-orange-900' :
                          'bg-gray-200 text-gray-600'}
                      `}>
                        {index + 1}
                      </div>
                      
                      {/* Rack info */}
                      <div>
                        <div className="font-semibold text-sm">Reol {rack.rackNumber}</div>
                        <div className="text-xs opacity-75">
                          {rack.locationCount} lokasjoner • {rack.eventCount} hendelser
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Score */}
                      <div className="text-right">
                        <div className="text-lg font-bold">{rack.score.toFixed(1)}</div>
                        <div className="text-xs opacity-75">{formatTimeAgo(rack.lastEventTime)}</div>
                      </div>
                      
                      {onRackClick && (
                        <ChevronRight size={16} className="opacity-50" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Info footer */}
      <Card className="p-3 bg-blue-50 border-blue-200">
        <div className="text-xs text-blue-800">
          <p className="font-medium mb-1">ℹ️ Aktivitetsrangering</p>
          <p>
            Rangeringen baseres på intensitetsscore som vekter forskjellige aktivitetstyper.
            Korreksjoner vektes høyere enn vanlig plukking/plassering.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default TopLocations;
